import { DoCheck, Component, ElementRef, ViewChild, ViewEncapsulation, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { isNullOrUndefined } from '@app/util/util';
import {
  FooterCategory, FoterLinks, FoterApps, FooterOperatedTypes, OPERATED_BY_OPEN_CERT_ID, FOOTER_OPERATED_BY_IMAGES
} from '@app/modules/core/footer/entities/footer-type';
import { environment as ENV } from '@environment';
import { Countries } from '@ux/entities/country.enum';
import { HomeFacade } from '@home/home.facade';
import { AssetTerm } from '@asset-terms';
import { specialTravelDataRootRoute } from '@app/modules/special-travel-data/special-travel-data.routing.module';
import { goToUrlHelper } from '@app/helpers/open-url.helper';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.container.html',
  styleUrls: ['./footer.container.sass'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class FooterContainer implements DoCheck {

  @Input() isMobile = false;

  @ViewChild('openOperatorCert') openOperatorCert: ElementRef;

  public readonly COMPLAINTS_BOOK: string = 'libro-reclamaciones.png';
  public readonly MINCETUR: string = 'MINCETUR.png';
  public readonly MINCETUR_PATH: string = 'web/travel/PE/FOOTER';

  public footerOperatedByImages: string = null;
  public showOperatiorCert: boolean = false;

  public categories: FooterCategory[] = [
    { title: 'FOOTER_MODULE.CATEGORIES.RESTAURANTS', link: ENV.footer_links.restaurants },
    { title: 'FOOTER_MODULE.CATEGORIES.MARKETS', link: ENV.footer_links.markets },
    { title: 'FOOTER_MODULE.CATEGORIES.COURIER', link: ENV.footer_links.courier },
    { title: 'FOOTER_MODULE.CATEGORIES.TRAVEL', link: ENV.footer_links.travel }
  ];

  public readonly android: FoterApps = ENV.app_android;
  public readonly ios: FoterApps = ENV.app_ios;
  public readonly huawei: FoterApps = ENV.app_huawei;
  public readonly facebookUrl: string = ENV.countryInfo.facebook;
  public readonly twitterUrl: string = ENV.countryInfo.twitter;
  public readonly instagramUrl: string = ENV.countryInfo.instagram;
  public readonly repentanceUrl: string = ENV.repentance_link_desktop;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  public readonly icons = Icon;
  public readonly iconFillColor = IconFillColor;

  public readonly imageSize = ImageSize;

  constructor(
    private readonly facade: HomeFacade,
  ) { }

  ngDoCheck(): void {
    if (this.openOperatorCert?.nativeElement?.querySelector(`.${OPERATED_BY_OPEN_CERT_ID}`)) {
      const span = this.openOperatorCert?.nativeElement?.querySelector(`.${OPERATED_BY_OPEN_CERT_ID}`);
      if (span && isNullOrUndefined(span.getAttribute('assignedlistener'))) {
        span.setAttribute('assignedlistener', true);
        span?.addEventListener('click', () =>
          this.operatorCertOpen(span.classList?.value?.replace(OPERATED_BY_OPEN_CERT_ID, '').trim() as FooterOperatedTypes));
      }
    }
  }

  get operatedBy$(): Observable<string> {
    return this.facade.homeData$.pipe(map(homeData =>
      homeData && homeData.footer_operated_by ? homeData.footer_operated_by : ENV.footer_operated_by
    ));
  }

  get rappiLegalLinks(): FoterLinks[] {
    return [
      {
        name: 'FOOTER_MODULE.NOTICES.TERMS_RAPPI',
        url: ENV.footer_links.terms_rappi,
        available: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.PRIVACY_RAPPI',
        url: ENV.footer_links.data_treatment_rappi,
        available: true,
      },
    ];
  }

  get travelLegalLinks(): FoterLinks[] {
    return [
      ...this.campaignsTerms,
      {
        name: this.isMobile ? 'FOOTER_MODULE.TITLES.TERMS_CONDITIONS' : 'FOOTER_MODULE.NOTICES.TERMS_NETACTICA',
        url: ENV.footer_links.terms_netactica,
        available: !this.isPE,
        partner: true,
        divider: true,
      },
      {
        name: this.isMobile ? 'FOOTER_MODULE.TITLES.PRIVACY_POLICY' : 'FOOTER_MODULE.NOTICES.PRIVACY_NETACTICA',
        url: ENV.footer_links.data_treatment_netactica,
        available: true,
        partner: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.SIC',
        url: ENV.footer_links.sic,
        available: this.showForCO,
        external: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.AEROCIVIL',
        url: ENV.footer_links.aerocivil,
        available: this.isAvailableAeroCivil,
        external: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.INQUIRIES_COMPLAINTS',
        url: ENV.countryInfo.inquiriesAndComplains,
        available: this.showForAR,
        external: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.RIGHT_REPENTANCE',
        url: ENV.footer_links.data_right_repentance_rappi,
        available: this.showForAR,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.RESPONSABLE_TOURISM',
        url: 'responsable-tourism',
        available: this.isPE,
      },
    ];
  }

  get footerLinks(): FoterLinks[] {
    return [
      {
        name: 'FOOTER_MODULE.NOTICES.RAPPITENDERO',
        url: ENV.footer_links.rappitendero,
        available: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.WORK_WITH_US',
        url: ENV.footer_links.job,
        available: true,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.REGISTER',
        url: ENV.footer_links.register_restaurant,
        available: this.showForCO,
      },
      {
        name: 'FOOTER_MODULE.NOTICES.RAPPIONE',
        url: ENV.footer_links.rappione,
        available: true,
      }
    ];
  }

  get showForCO(): boolean {
    return ENV.countryInfo.iso_code === Countries.Colombia;
  }

  get showForAR(): boolean {
    return ENV.countryInfo.iso_code === Countries.Argentina;
  }

  get year(): number {
    return new Date().getFullYear();
  }

  get isAvailableAeroCivil(): boolean {
    return this.showForCO || ENV.countryInfo.iso_code === Countries.Mexico;
  }

  get urlComplaintsBook(): string {
    return ENV.countryInfo.complaints_book;
  }

  get urlFooterDocPDF(): string {
    return ENV.countryInfo.footer_pdf;
  }

  get isPE(): boolean {
    return ENV.countryInfo.iso_code === Countries.Peru;
  }

  get btnRepentance(): boolean {
    return ENV.btn_repentance;
  }

  private get campaignsTerms(): FoterLinks[] {
    return AssetTerm.
      SUMMARY_TERMS_AND_POLICIES.CAMPAIGNS_TERMS?.map(
        item => ({
          name: item.HREF?.TEXT,
          url: specialTravelDataRootRoute,
          state: item.HREF.STATE,
          available: this.campaignsTermsInFooter && !item.DISABLED,
        })
      );
  }

  private get campaignsTermsInFooter(): boolean {
    return AssetTerm.SUMMARY_TERMS_AND_POLICIES.CAMPAIGNS_TERMS_IN_FOOTER;
  }


  public toggleOperatiorCert(): void {
    this.showOperatiorCert = !this.showOperatiorCert;
  }

  private operatorCertOpen(type: FooterOperatedTypes): void {
    switch (type) {
      case FooterOperatedTypes.CO_RNT_79000:
        this.footerOperatedByImages = FOOTER_OPERATED_BY_IMAGES[type];
        break;
    }
    this.toggleOperatiorCert();
  }

  public goToLink(url: string): void {
    goToUrlHelper(url);
  }

  public trackById(index: number, item: FoterLinks | FooterCategory): string {
    return `${index}__${(item as FoterLinks).name ?? (item as FooterCategory).title}`;
  }

}
